import config from '@/config';
import { AuthSessionExpiredError } from '@/features/Keycloak/keycloakService';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { GraphQLError } from 'graphql';
import { ReactNode } from 'react';
import { toast } from 'sonner';

type ToastSuppressionMeta = {
  /**
   * When true, the global QueryClient onError handlers will not show a toast.
   * Useful when a screen handles errors itself (inline UI, silent retry, etc.).
   */
  suppressErrorToast?: boolean;
};

function shouldSuppressErrorToast(meta: unknown): boolean {
  return Boolean(
    (meta as ToastSuppressionMeta | undefined)?.suppressErrorToast,
  );
}

/**
 * Extension keys that only carry debugging detail (HotChocolate adds them
 * when exception details are enabled). Hidden in production builds.
 */
const DEV_ONLY_EXTENSION_KEYS = new Set(['stackTrace']);

function formatExtensionValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  // Nested objects (Hasura `internal`, server `context`, ...) are shown as
  // pretty-printed JSON so nothing in the payload is lost.
  return JSON.stringify(value, null, 2);
}

/**
 * Renders the full content of an error for the toast body. For a GraphQL
 * error that is the top-level message plus every entry of `extensions`,
 * whatever the server put there (Hasura: `code`, `path`, `internal`;
 * the .NET server: `code`, `kind`, `context`, and in dev `message`,
 * `stackTrace`).
 */
export function describeError(error: Error): ReactNode {
  if (!(error instanceof GraphQLError)) {
    return error.message ? <p>{error.message}</p> : null;
  }

  const extensions = Object.entries(error.extensions ?? {}).filter(
    ([key, value]) =>
      value !== undefined &&
      value !== null &&
      !(config.production && DEV_ONLY_EXTENSION_KEYS.has(key)),
  );

  return (
    <div className="space-y-1 break-words">
      <p>{error.message}</p>
      {extensions.map(([key, value]) => {
        const text = formatExtensionValue(value);
        const isMultiline = text.includes('\n');
        return (
          <div key={key}>
            <span className="font-medium">{key}: </span>
            {isMultiline ? (
              <pre className="max-h-40 overflow-auto text-xs whitespace-pre-wrap">
                {text}
              </pre>
            ) : (
              <span>{text}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function showErrorToast(error: Error, meta: unknown) {
  if (error instanceof AuthSessionExpiredError) {
    return;
  }

  if (shouldSuppressErrorToast(meta)) {
    return;
  }

  toast.error(error.name, {
    duration: 3000,
    description: describeError(error),
  });
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      showErrorToast(error, mutation?.meta);
    },
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      showErrorToast(error, query?.meta);
    },
  }),
});
